// src/mobile_pages/materials/inout/In_new.jsx

import React, { useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import { useLocation } from "react-router-dom";
import axios from "../../../api/axios";
import InSummary from "../../../components/common/materials/InSummary";
import { getStockSummary } from "../../../api/material/extras";

const TAX_RATE = 0.1;
const CURRENCY = "KRW";

export default function InOfficialForm({
  initial = {},
  projectId,
  docLabel = "입고서",
  readOnly = false,
  onChange,
}) {
  // -----------------------------
  // location.state 데이터 수신(PO → GR)
  // -----------------------------
  const location = useLocation();
  const poHeader = location.state?.poHeader;
  const poItems = location.state?.poItems;
  const fromDns = location.state?.from;

  // -----------------------------
  // 기본 Header
  // -----------------------------
  const initialHeader = initial.header || initial;

  const [title, setTitle] = useState(
    poHeader?.title
      ? `[입고] ${poHeader.title}`
      : initialHeader.title || "자재 입고서(공식)"
  );
  const [inDate, setInDate] = useState(
    initialHeader.in_date ||
    initial.in_date ||
    new Date().toISOString().slice(0, 10)
  );
  const [supplier, setSupplier] = useState(
    poHeader?.supplier || initialHeader.supplier || ""
  );
  const [note, setNote] = useState(initialHeader.note || "");

  // -----------------------------
  // PO 목록 불러오기
  // -----------------------------
  const [poList, setPoList] = useState([]);
  const [selectedPO, setSelectedPO] = useState(fromDns || null);

  useEffect(() => {
    async function loadPOList() {
      try {
        const res = await axios.get(`/material/docs/`, {
          params: { project: projectId, form_type: "PO" },
        });
        setPoList(res.data?.results || res.data || []);
      } catch (err) {
        console.error("❌ PO 목록 로드 실패", err);
      }
    }
    loadPOList();
  }, [projectId]);

  // -----------------------------
  // PO 내용 불러오기
  // -----------------------------
  const loadFromPO = async (dns) => {
    try {
      const res = await axios.get(`/material/docs/${dns}/`);
      const doc = res.data;

      const header = doc.header || {};
      const items = doc.lines?.map((l) => l.body || l) || [];

      setSelectedPO(dns);
      setTitle(`[입고] ${header.title || header.note || ""}`);
      setSupplier(header.supplier || "");

      setItems(
        items.map((r) => ({
          _id: uuid(),
          item_id: null,
          code: r.code,
          name: r.name,
          spec: r.spec,
          unit: r.unit,
          qty: r.qty,
          price: r.price,
          supply: r.supply,
          vat: r.vat,
          total: r.total,
          lot: "",
          remark: "",
        }))
      );
    } catch (err) {
      console.error("❌ PO 상세 불러오기 실패", err);
    }
  };

  // -----------------------------
  // 되돌리기 기능
  // -----------------------------
  const resetForm = () => {
    setSelectedPO(null);
    setTitle("자재 입고서(공식)");
    setInDate(new Date().toISOString().slice(0, 10));
    setSupplier("");
    setNote("");
    setItems([makeEmptyRow()]);
  };

  // 자동완성 (기존 그대로)
  const [acQuery, setAcQuery] = useState("");
  const [acList, setAcList] = useState([]);
  const [acLoading, setAcLoading] = useState(false);
  const [acTarget, setAcTarget] = useState(null);

  const fetchAuto = async (q) => {
    if (!q || q.length < 1) {
      setAcList([]);
      return;
    }

    setAcLoading(true);
    try {
      const data = await getStockSummary({ q });
      setAcList(data?.items || []);
    } catch (e) {
      console.error("AC fetch error", e);
    } finally {
      setAcLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => fetchAuto(acQuery), 200);
    return () => clearTimeout(t);
  }, [acQuery]);

  // -----------------------------
  // Row 구조
  // -----------------------------
  const makeEmptyRow = () => ({
    _id: uuid(),
    item_id: null,
    code: "",
    name: "",
    spec: "",
    unit: "EA",
    qty: 0,
    price: 0,
    supply: 0,
    vat: 0,
    total: 0,
    lot: "",
    remark: "",
  });

  const mappedInitial = poItems
    ? poItems.map((r) => ({
      _id: uuid(),
      item_id: null,
      code: r.code,
      name: r.name,
      spec: r.spec,
      unit: r.unit,
      qty: r.qty,
      price: r.price,
      supply: r.supply,
      vat: r.vat,
      total: r.total,
      lot: "",
      remark: "",
    }))
    : (initial.items || initial.lines || []).map((r) => ({
      ...r,
      _id: r._id || uuid(),
    }));

  const [items, setItems] = useState(
    mappedInitial.length > 0 ? mappedInitial : [makeEmptyRow()]
  );

  const rows = useMemo(
    () =>
      items.map((r) => {
        const qty = Number(r.qty || 0);
        const price = Number(r.price || 0);
        const supply = +(qty * price).toFixed(2);
        const vat = +(supply * TAX_RATE).toFixed(2);
        const total = +(supply + vat).toFixed(2);
        return { ...r, supply, vat, total };
      }),
    [items]
  );

  const subtotal = useMemo(
    () => rows.reduce((s, r) => s + Number(r.supply || 0), 0),
    [rows]
  );
  const vatTotal = useMemo(
    () => rows.reduce((s, r) => s + Number(r.vat || 0), 0),
    [rows]
  );
  const grandTotal = +(subtotal + vatTotal).toFixed(2);

  // ✅ 요약 카드용 payload
  const summaryForm = useMemo(
    () => ({
      title,
      in_date: inDate,
      supplier,
      note,
      items: rows.map(({ _id, ...rest }) => rest),
      subtotal,
      vat_total: vatTotal,
      total: grandTotal,
    }),
    [title, inDate, supplier, note, rows, subtotal, vatTotal, grandTotal]
  );

  // 🔥 상위로 payload 전달 (공통 규격)
  useEffect(() => {
    if (!onChange) return;

    onChange({
      id: initial.id,
      type: "in",
      project: projectId,
      header: {
        title,
        in_date: inDate,
        supplier,
        note,
        from_po_dns: selectedPO,
        currency: CURRENCY,
        tax_rate: TAX_RATE,
      },
      items: rows.map(({ _id, ...rest }) => rest),
      subtotal,
      vat_total: vatTotal,
      total: grandTotal,
      summary_component: <InSummary form={summaryForm} />,
      docDescribe:
        "발주서를 선택하여 입고할 품목을 확인하고 수량을 조정할 수 있습니다.",
    });
  }, [
    title,
    inDate,
    supplier,
    note,
    rows,
    subtotal,
    vatTotal,
    grandTotal,
    selectedPO,
    projectId,
    onChange,
    summaryForm,
  ]);

  const addRow = () => setItems((p) => [...p, makeEmptyRow()]);
  const removeRow = (id) =>
    setItems((p) => {
      const filtered = p.filter((r) => r._id !== id);
      return filtered.length > 0 ? filtered : [makeEmptyRow()];
    });
  const updateCell = (id, key, value) =>
    setItems((p) => p.map((r) => (r._id === id ? { ...r, [key]: value } : r)));

  return (
    <div
      className="mobile-page-root mb-16"
      style={{ width: '100%', maxWidth: '100vw', minWidth: 0, display: 'flex', justifyContent: 'center', overflowX: 'hidden', boxSizing: 'border-box' }}
    >
      <div
        className="mobile-page-root-inner space-y-3"
        style={{ width: '100%', maxWidth: '480px', margin: '0 auto', padding: '0 1rem 1rem', boxSizing: 'border-box', overflowX: 'hidden' }}
      >{/* 🔹 PO 선택 + 되돌리기 */}
        {!readOnly && (
          <div className="flex flex-col gap-2 min-w-0">
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm min-w-0"
              value={selectedPO || ""}
              onChange={(e) => e.target.value && loadFromPO(e.target.value)}
            >
              <option value="" disabled>
                발주서 선택
              </option>
              {poList.map((p) => (
                <option key={p.dns} value={p.dns}>
                  {p.header?.title || p.dns}
                </option>
              ))}
            </select>

            {selectedPO && (
              <button className="btn-basic-sm w-full" onClick={resetForm}>
                되돌리기
              </button>
            )}
          </div>
        )}

        {/* 기본 정보 */}
        <div className="space-y-1">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">제목</label>
            <input
              className="w-full border rounded-md px-2 py-1 text-sm"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={readOnly}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">입고일</label>
            <input
              type="date"
              className="w-full border rounded-md px-2 py-1 text-sm"
              value={inDate}
              onChange={(e) => setInDate(e.target.value)}
              disabled={readOnly}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">
              공급업체
            </label>
            <input
              className="w-full border rounded-md px-2 py-1 text-sm"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              disabled={readOnly}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">
              비고 / 메모
            </label>
            <textarea
              className="w-full min-h-[100px] border rounded-md px-2 py-1 text-sm"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={readOnly}
            />
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden min-w-0">
          <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 min-w-0">
            <div className="font-medium text-sm min-w-0 truncate">
              입고 품목
            </div>
            {!readOnly && (
              <button
                className="btn-create-sm flex-shrink-0 whitespace-nowrap"
                onClick={addRow}
              >
                + 행 추가
              </button>
            )}
          </div>
          <div className="space-y-3">
            {rows.map((r) => (
              <div
                key={r._id}
                className="border rounded-lg p-3 bg-white space-y-2 relative"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500">
                    품목 정보
                  </span>
                  {!readOnly && (
                    <button
                      className="px-2 py-1 rounded border text-xs whitespace-nowrap text-red-600 hover:bg-red-50"
                      onClick={() => removeRow(r._id)}
                    >
                      삭제
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {/* 코드 */}
                  <div className="space-y-1 relative">
                    <label className="text-[10px] font-medium text-gray-500 block">
                      코드
                    </label>
                    <input
                      className="w-full rounded border px-2 py-1.5 text-xs min-w-0"
                      value={r.code || ""}
                      onChange={(e) => {
                        updateCell(r._id, "code", e.target.value);
                        setAcQuery(e.target.value);
                        setAcTarget(r._id);
                      }}
                      onFocus={() => {
                        setAcTarget(r._id);
                        setAcQuery(r.code || "");
                      }}
                      disabled={readOnly}
                      placeholder="코드 입력"
                    />
                    {/* 자동완성 박스 */}
                    {acTarget === r._id && acList.length > 0 && !readOnly && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white border rounded-lg shadow z-50 max-h-60 overflow-auto">
                        {acLoading && (
                          <div className="px-3 py-2 text-sm text-gray-400">
                            검색 중…
                          </div>
                        )}
                        {acList.map((m) => (
                          <div
                            key={m.item_id}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex justify-between"
                            onClick={() => {
                              updateCell(r._id, "item_id", m.item_id);
                              updateCell(r._id, "code", m.item_code);
                              updateCell(r._id, "name", m.item_name);
                              updateCell(r._id, "spec", m.spec || "");
                              updateCell(r._id, "unit", m.unit || "");
                              updateCell(r._id, "price", m.unit_price || 0);
                              setAcList([]);
                            }}
                          >
                            <div>
                              <div className="font-medium text-xs">
                                {m.item_code}
                              </div>
                              <div className="text-[10px] text-gray-500">
                                {m.item_name}
                              </div>
                            </div>
                            <div className="text-right text-[10px] text-gray-500">
                              재고 {Number(m.qty_on_hand).toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 품명 */}
                  <div className="space-y-1 relative">
                    <label className="text-[10px] font-medium text-gray-500 block">
                      품명
                    </label>
                    <input
                      className="w-full rounded border px-2 py-1.5 text-xs min-w-0"
                      value={r.name || ""}
                      onChange={(e) => {
                        updateCell(r._id, "name", e.target.value);
                        setAcQuery(e.target.value);
                        setAcTarget(r._id);
                      }}
                      onFocus={() => {
                        setAcTarget(r._id);
                        setAcQuery(r.name || "");
                      }}
                      disabled={readOnly}
                      placeholder="품명 입력"
                    />
                  </div>

                  {/* 규격 */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">
                      규격
                    </label>
                    <input
                      className="w-full rounded border px-2 py-1.5 text-xs min-w-0"
                      value={r.spec || ""}
                      onChange={(e) =>
                        updateCell(r._id, "spec", e.target.value)
                      }
                      disabled={readOnly}
                      placeholder="규격"
                    />
                  </div>

                  {/* 단위 */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">
                      단위
                    </label>
                    <input
                      className="w-full rounded border px-2 py-1.5 text-xs min-w-0"
                      value={r.unit || ""}
                      onChange={(e) =>
                        updateCell(r._id, "unit", e.target.value)
                      }
                      disabled={readOnly}
                      placeholder="단위"
                    />
                  </div>

                  {/* 수량 */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">
                      수량
                    </label>
                    <input
                      type="number"
                      className="w-full rounded border px-2 py-1.5 text-right text-xs min-w-0"
                      value={r.qty}
                      onChange={(e) => updateCell(r._id, "qty", e.target.value)}
                      disabled={readOnly}
                      placeholder="수량"
                    />
                  </div>

                  {/* 단가 */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">
                      단가
                    </label>
                    <input
                      type="number"
                      className="w-full rounded border px-2 py-1.5 text-right text-xs min-w-0"
                      value={r.price}
                      onChange={(e) =>
                        updateCell(r._id, "price", e.target.value)
                      }
                      disabled={readOnly}
                      placeholder="단가"
                    />
                  </div>

                  {/* 공급가 */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">
                      공급가
                    </label>
                    <div className="text-xs text-right px-2 py-1.5 bg-gray-50 rounded border">
                      {Number(r.supply).toLocaleString()}
                    </div>
                  </div>

                  {/* 세액 */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">
                      세액
                    </label>
                    <div className="text-xs text-right px-2 py-1.5 bg-gray-50 rounded border">
                      {Number(r.vat).toLocaleString()}
                    </div>
                  </div>

                  {/* 합계 */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">
                      합계
                    </label>
                    <div className="text-xs text-right px-2 py-1.5 bg-gray-50 rounded border">
                      {Number(r.total).toLocaleString()}
                    </div>
                  </div>

                  {/* LOT */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">
                      LOT
                    </label>
                    <input
                      className="w-full rounded border px-2 py-1.5 text-xs min-w-0"
                      value={r.lot || ""}
                      onChange={(e) => updateCell(r._id, "lot", e.target.value)}
                      disabled={readOnly}
                      placeholder="LOT"
                    />
                  </div>
                </div>
              </div>
            ))}
            {rows.length > 0 && (
              <div className="border-t-2 border-gray-300 pt-2 mt-2">
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2 text-right font-medium text-sm mb-1">
                    합계
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">
                      공급가
                    </label>
                    <div className="text-xs text-right px-2 py-1.5 bg-gray-50 rounded border font-semibold">
                      {subtotal.toLocaleString()}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">
                      세액
                    </label>
                    <div className="text-xs text-right px-2 py-1.5 bg-gray-50 rounded border font-semibold">
                      {vatTotal.toLocaleString()}
                    </div>
                  </div>
                  <div className="space-y-1 col-span-2">
                    <label className="text-[10px] font-medium text-gray-500 block">
                      합계
                    </label>
                    <div className="text-xs text-right px-2 py-1.5 bg-gray-50 rounded border font-semibold">
                      {grandTotal.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
